import React, { useState, useRef } from 'react';
import './CSVUploadModal.css';
import { HiOutlineX } from 'react-icons/hi';
import { downloadSampleExcelTemplate } from '../../utils/excelExportUtils';
import { matchAddresses, MatchAddressesResult, MatchClientInput } from '../../services/storeIdService';
import * as XLSX from 'xlsx-js-style';

interface StoreData {
  brand_name: string;
  id: string;
  address: string;
  zipcode: string;
}


interface ExcelUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (result: MatchAddressesResult) => void;
  brandName?: string;
}

export default function ExcelUploadModal({ isOpen, onClose, onImport, brandName }: ExcelUploadModalProps) {
  const [excelData, setExcelData] = useState<StoreData[]>([]);
  const [matchResult, setMatchResult] = useState<MatchAddressesResult | null>(null);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateRow = (row: StoreData, rowNumber: number): string | null => {
    if (!row.brand_name || String(row.brand_name).trim() === '') {
      return `Row ${rowNumber}: Brand name is required`;
    }
    if (!row.address || String(row.address).trim() === '') {
      return `Row ${rowNumber}: Address is required`;
    }
    if (!row.zipcode || String(row.zipcode).trim() === '') {
      return `Row ${rowNumber}: Zip code is required`;
    }
    return null;
  };

  const normalizeRowKeys = (row: any): Record<string, any> => {
    const normalized: Record<string, any> = {};
    for (const key of Object.keys(row)) {
      // "Brand Name" -> "brand_name", "Zip Code" -> "zip_code", "Store Id" -> "store_id"
      const normKey = key.trim().toLowerCase().replace(/\s+/g, '_');
      normalized[normKey] = row[key];
    }
    return normalized;
  };

  const parseExcel = (buffer: ArrayBuffer): StoreData[] => {
    const workbook = XLSX.read(buffer, { type: 'array' });
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const data = XLSX.utils.sheet_to_json<any>(worksheet);

    const rows: StoreData[] = [];
    const errors: string[] = [];

    data.forEach((row, index) => {
      const r = normalizeRowKeys(row);
      const storeData: StoreData = {
        brand_name: String(r.brand_name || '').trim(),
        id: String(r.store_id || r.id || r.store_number || '').trim(),
        address: String(r.address || '').trim(),
        zipcode: String(r.zip_code || r.zipcode || '').trim(),
      };

      const error = validateRow(storeData, index + 2);
      if (error) {
        errors.push(error);
      } else {
        rows.push(storeData);
      }
    });

    if (rows.length === 0) {
      throw new Error('Excel file contains no valid data rows');
    }

    return rows;
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError('');
    setMatchResult(null);
    setLoading(true);

    try {
      const buffer = await file.arrayBuffer();
      const data = parseExcel(buffer);
      setExcelData(data);

      const clients: MatchClientInput[] = data.map(row => ({
        id: row.id,
        address: row.address,
        zipcode: row.zipcode,
      }));

      const result = await matchAddresses(clients, brandName);
      setMatchResult(result);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to process Excel file';
      if (msg.length > 100) {
        console.error('Matching error:', msg);
        setError('Something went wrong. Please try again.');
      } else {
        setError(msg);
      }
      setExcelData([]);
      setMatchResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (!matchResult) {
      setError('Please upload an Excel file');
      return;
    }
    onImport(matchResult);
    setExcelData([]);
    setMatchResult(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="csv-modal-overlay">
      <div className="csv-modal">
        <div className="csv-modal-header">
          <h2>Import Store IDs from Excel</h2>
          <button onClick={onClose} className="csv-close-btn">
            <HiOutlineX />
          </button>
        </div>

        <div className="csv-modal-body">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          <p className="csv-template-link">
            You can download the Excel template <button
              onClick={downloadSampleExcelTemplate}
              className="csv-template-download-btn"
            >
              here
            </button>.
          </p>

          <button
            onClick={() => fileInputRef.current?.click()}
            className="csv-upload-btn"
            disabled={loading}
          >
            {loading ? 'Matching...' : 'Choose Excel File'}
          </button>

          {matchResult && (
            <div className="csv-preview">
              <p>{excelData.length} records uploaded</p>
              <p className="csv-match-count">
                Matched: <strong>{matchResult.matched_count}</strong>
              </p>
              {matchResult.unmatched_client_count > 0 && (
                <p>Unmatched: {matchResult.unmatched_client_count}</p>
              )}
            </div>
          )}

          {error && <div className="csv-error">{error}</div>}
        </div>

        <div className="csv-modal-footer">
          <button onClick={onClose} className="csv-btn-cancel">Cancel</button>
          <button
            onClick={handleApply}
            disabled={!matchResult || loading}
            className="csv-btn-import"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
